import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, useMap, FeatureGroup, Tooltip } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { cellToBoundary } from 'h3-js';
import { X, Crosshair, MapPin, RotateCcw, PanelRightOpen, PanelRightClose, Filter, Sun, Moon, Globe, Download, Upload, FileDown, Eye, EyeOff, Edit3, Save, Trash2 } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { DateRangePicker } from './DateRangePicker';
import JSZip from 'jszip';
import * as turf from '@turf/turf';

// Service Area center
const SERVICE_AREA_CENTER = [13.075, 80.21];
const SERVICE_AREA_ZOOM = 13;
const DEFAULT_OPACITY = 0.7;

// Default Weird Area polygon coordinates (from weird.kmz)
const defaultWeirdAreaCoordinates = [
  [13.1015893, 80.1607487],
  [13.0599548, 80.1602337],
  [13.0673783, 80.1770722],
  [13.0307484, 80.1925271],
  [13.032529, 80.2548191],
  [13.0831765, 80.2771136],
  [13.0921219, 80.2764055],
  [13.1041969, 80.2225403],
  [13.1015893, 80.1607487]
];

// Service Area polygon coordinates from KML (Fish Bowl)
const serviceAreaCoordinates = [
  [13.0607455, 80.1604917], [13.0635885, 80.1675297], [13.0645915, 80.1714777],
  [13.067046, 80.176235], [13.065167, 80.17771], [13.064756, 80.179072],
  [13.0627924, 80.1802961], [13.060211, 80.181901], [13.0594632, 80.1832028],
  [13.05859, 80.184408], [13.054517, 80.1867691], [13.0510675, 80.1880929],
  [13.0477898, 80.1893604], [13.0462802, 80.1898457], [13.0409328, 80.1907344],
  [13.0385044, 80.1921629], [13.0370829, 80.1932784], [13.0364555, 80.1973125],
  [13.0359953, 80.2007458], [13.0354937, 80.2050802], [13.0350756, 80.2078483],
  [13.0349083, 80.2106163], [13.0344066, 80.214736], [13.0340094, 80.2178257],
  [13.0338212, 80.2208726], [13.0336332, 80.2247136], [13.0341141, 80.2270097],
  [13.0343442, 80.228512], [13.0349086, 80.2313016], [13.0360561, 80.2338506],
  [13.0362116, 80.2370135], [13.037956, 80.239833], [13.04026, 80.2430603],
  [13.042204, 80.2455064], [13.0444824, 80.24821], [13.0485376, 80.2515574],
  [13.053889, 80.2567933], [13.0558539, 80.2587887], [13.0574843, 80.2599259],
  [13.0602434, 80.2615137], [13.0631701, 80.2624581], [13.0665985, 80.2628016],
  [13.068396, 80.2627371], [13.0696083, 80.2626725], [13.0712805, 80.2624576],
  [13.0731302, 80.2620071], [13.0745618, 80.2615565], [13.0761711, 80.2610845],
  [13.0777386, 80.2606125], [13.0801003, 80.2596684], [13.0824585, 80.2580955],
  [13.0839302, 80.2568799], [13.0853183, 80.2555355], [13.0885823, 80.2539327],
  [13.0926027, 80.2515025], [13.0975315, 80.2470086], [13.0980325, 80.2444336],
  [13.1000375, 80.2380406], [13.101718, 80.2326381], [13.104451, 80.2283351],
  [13.105603, 80.2255781], [13.106841, 80.2206681], [13.10665, 80.2159531],
  [13.107941, 80.2101171], [13.106835, 80.1980861], [13.105622, 80.1938202],
  [13.101454, 80.1880991], [13.1005405, 80.1846957], [13.0988685, 80.1766277],
  [13.0993705, 80.1683877], [13.1015435, 80.1618647], [13.0880005, 80.1613497],
  [13.0607455, 80.1604917]
];

// Create demand label
const createDemandLabel = (demand, color) => {
  const lightBgColors = ['#38BDF8', '#2DD4BF', '#86EFAC', '#FDE047', '#FB923C'];
  const isLightBg = lightBgColors.includes(color);
  const textColor = isLightBg ? '#1a1a1a' : '#ffffff';

  return L.divIcon({
    className: 'demand-label',
    html: `<div style="font-size: 12px; font-weight: 700; color: ${textColor}; text-shadow: ${isLightBg ? 'none' : '0 1px 2px rgba(0,0,0,0.5)'}; text-align: center;">${demand}</div>`,
    iconSize: [30, 20],
    iconAnchor: [15, 10]
  });
};

// Map Controls Component - MUST be inside MapContainer
const MapControls = ({
  showServiceArea, setShowServiceArea,
  showWeirdArea, setShowWeirdArea,
  hexOpacity, setHexOpacity,
  isEditMode, setIsEditMode,
  onExportKmz,
  kmzOverlay, onKmzUpload, onKmzToggle, onKmzRemove,
  onImportWeirdAreaKmz,
  weirdAreaDemandSum
}) => {
  const map = useMap();
  const fileInputRef = useRef(null);
  const weirdAreaFileInputRef = useRef(null);
  const controlsRef = useRef(null);

  // Prevent map click propagation for controls
  useEffect(() => {
    if (controlsRef.current) {
      L.DomEvent.disableClickPropagation(controlsRef.current);
      L.DomEvent.disableScrollPropagation(controlsRef.current);
    }
  }, []);

  const handleRecentre = () => {
    map.setView(SERVICE_AREA_CENTER, SERVICE_AREA_ZOOM, { animate: true });
  };

  const resetOpacity = () => {
    setHexOpacity(DEFAULT_OPACITY);
  };

  // Handle Weird Area KMZ import
  const handleWeirdAreaKmzChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      // Find KML file in KMZ
      let kmlContent = null;
      for (const filename of Object.keys(zip.files)) {
        if (filename.endsWith('.kml')) {
          kmlContent = await zip.files[filename].async('string');
          break;
        }
      }

      if (!kmlContent) {
        alert('No KML file found in KMZ');
        return;
      }

      // Parse KML to extract coordinates
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');

      // Find all coordinate elements
      const coordinateElements = kmlDoc.getElementsByTagName('coordinates');
      const polygons = [];

      for (let i = 0; i < coordinateElements.length; i++) {
        const coordText = coordinateElements[i].textContent.trim();
        const coords = coordText.split(/\s+/).map(coord => {
          const [lng, lat] = coord.split(',').map(Number);
          if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            return [lat, lng]; // Convert to Leaflet format [lat, lng]
          }
          return null;
        }).filter(c => c !== null);

        if (coords.length >= 3) {
          polygons.push(coords);
        }
      }

      if (polygons.length > 0) {
        // Call the import handler with parsed coordinates
        if (polygons.length === 1) {
          onImportWeirdAreaKmz({ type: 'single', coordinates: polygons[0] });
        } else {
          onImportWeirdAreaKmz({ type: 'multi', coordinates: polygons });
        }
        // Show the weird area after import
        if (!showWeirdArea) {
          setShowWeirdArea(true);
        }
      } else {
        alert('No valid polygon coordinates found in KMZ file');
      }
    } catch (error) {
      console.error('Error parsing KMZ:', error);
      alert('Error parsing KMZ file. Please ensure it\'s a valid KMZ/KML file.');
    }

    // Reset file input
    e.target.value = '';
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      let kmlContent = null;
      for (const filename of Object.keys(zip.files)) {
        if (filename.endsWith('.kml')) {
          kmlContent = await zip.files[filename].async('string');
          break;
        }
      }

      if (!kmlContent) {
        alert('No KML file found in KMZ');
        return;
      }

      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');

      const polygons = [];
      const placemarks = kmlDoc.getElementsByTagName('Placemark');

      for (let i = 0; i < placemarks.length; i++) {
        const placemark = placemarks[i];
        const nameEl = placemark.getElementsByTagName('name')[0];
        const name = nameEl ? nameEl.textContent : `Polygon ${i + 1}`;

        const coordsElements = placemark.getElementsByTagName('coordinates');
        for (let j = 0; j < coordsElements.length; j++) {
          const coordsText = coordsElements[j].textContent.trim();
          const coords = coordsText.split(/\s+/).map(coord => {
            const [lng, lat] = coord.split(',').map(Number);
            return [lat, lng];
          }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));

          if (coords.length > 2) {
            polygons.push({ name, coordinates: coords });
          }
        }
      }

      if (polygons.length === 0) {
        alert('No valid polygons found in KMZ file');
        return;
      }

      onKmzUpload({
        filename: file.name,
        polygons,
        points: [],
        visible: true
      });

    } catch (error) {
      console.error('Error parsing KMZ:', error);
      alert('Error parsing KMZ file: ' + error.message);
    }

    e.target.value = '';
  };

  return (
    <>
      {/* Right side controls panel */}
      <div ref={controlsRef} className="absolute top-4 right-4 z-[5000] flex flex-col gap-2 pointer-events-auto">
        {/* Recentre Button */}
        <button
          onClick={handleRecentre}
          className="bg-white hover:bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 shadow-lg flex items-center gap-2 text-sm font-medium text-slate-700 transition-all hover:shadow-xl"
          title="Recentre to Service Area"
        >
          <Crosshair className="w-4 h-4 text-indigo-600" />
          Recentre
        </button>

        {/* Fish Bowl Toggle */}
        <button
          onClick={() => setShowServiceArea(prev => !prev)}
          className={`px-3 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 shadow-lg ${showServiceArea
            ? 'bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100'
            : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          title="Toggle Service Area"
        >
          <MapPin className="w-4 h-4" />
          Fish Bowl: {showServiceArea ? 'ON' : 'OFF'}
        </button>

        {/* Weird Area Toggle & Import */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Weird Area
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => setShowWeirdArea(prev => !prev)}
              className={`w-full px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${showWeirdArea
                ? 'bg-gray-800 text-white hover:bg-gray-900'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-300'
                }`}
              title="Toggle Weird Area"
            >
              {showWeirdArea ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {showWeirdArea ? 'Hide Weird Area' : 'Show Weird Area'}
            </button>

            {/* Import KMZ for Weird Area */}
            <input
              ref={weirdAreaFileInputRef}
              type="file"
              accept=".kmz,.kml"
              onChange={handleWeirdAreaKmzChange}
              className="hidden"
            />
            <button
              onClick={() => weirdAreaFileInputRef.current?.click()}
              className="w-full px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-xs font-medium text-slate-600 transition-colors flex items-center justify-center gap-1.5"
            >
              <Upload className="w-3.5 h-3.5" />
              Import KMZ
            </button>

            {/* Demand Sum Display - only show when weird area is visible */}
            {showWeirdArea && weirdAreaDemandSum && weirdAreaDemandSum.total > 0 && (
              <div className="bg-gray-900 text-white rounded-lg p-2.5 mt-1">
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                  Total Demand Inside
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xl font-bold text-emerald-400">
                    {weirdAreaDemandSum.total.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    ({weirdAreaDemandSum.hexCount} hexes)
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Service Area Editor */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Service Area Editor
          </div>

          {!isEditMode ? (
            <button
              onClick={() => setIsEditMode(true)}
              className="w-full px-3 py-2 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-lg text-xs font-medium text-amber-700 transition-colors flex items-center justify-center gap-1.5"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Rework Service Area
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-amber-600 font-medium text-center py-1 bg-amber-50 rounded">
                ✏️ Drawing Mode Active
              </div>
              <div className="text-[10px] text-slate-500 text-center">
                Draw polygons to add to area
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleToggleEditMode(false)}
                  className="flex-1 px-2 py-1.5 bg-green-100 hover:bg-green-200 rounded text-xs font-medium text-green-700 transition-colors flex items-center justify-center gap-1"
                >
                  <Save className="w-3 h-3" />
                  Save
                </button>
                <button
                  onClick={() => { setDrawnLayers([]); if (featureGroupRef.current) featureGroupRef.current.clearLayers(); setIsEditMode(false); }}
                  className="px-2 py-1.5 bg-red-100 hover:bg-red-200 rounded text-xs font-medium text-red-700 transition-colors"
                  title="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {showWeirdArea && !isEditMode && (
            <button
              onClick={onExportKmz}
              className="w-full mt-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg text-xs font-medium text-slate-700 transition-colors flex items-center justify-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Export as KMZ
            </button>
          )}
        </div>

        {/* KMZ Overlay Upload */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            KMZ Overlay
          </div>

          {kmzOverlay ? (
            <div className="space-y-2">
              <div className="text-xs text-slate-600 truncate max-w-[150px]" title={kmzOverlay.filename}>
                {kmzOverlay.filename}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={onKmzToggle}
                  className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${kmzOverlay.visible
                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  title={kmzOverlay.visible ? 'Hide overlay' : 'Show overlay'}
                >
                  {kmzOverlay.visible ? <Eye className="w-3.5 h-3.5 mx-auto" /> : <EyeOff className="w-3.5 h-3.5 mx-auto" />}
                </button>
                <button
                  onClick={onKmzRemove}
                  className="px-2 py-1.5 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                  title="Remove overlay"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".kmz,.kml"
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-3 py-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg text-xs font-medium text-orange-700 transition-colors flex items-center justify-center gap-1.5"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload KMZ
              </button>
            </>
          )}
        </div>

        {/* Transparency Slider */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Hex Opacity
            </label>
            <button
              onClick={resetOpacity}
              className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-indigo-600 transition-colors"
              title="Reset to default (70%)"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={hexOpacity * 100}
            onChange={(e) => setHexOpacity(parseInt(e.target.value) / 100)}
            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span>0%</span>
            <span className="font-bold text-indigo-600">{Math.round(hexOpacity * 100)}%</span>
            <span>100%</span>
          </div>
        </div>
      </div>

      {/* Bottom Legend - positioned on the LEFT side */}
      <div className="absolute bottom-6 left-4 bg-white/95 backdrop-blur p-3 rounded-lg border border-slate-200 text-xs shadow-lg z-[999]">
        <div className="font-semibold mb-2 text-slate-700">Demand (Low → High)</div>
        <div className="flex gap-1 items-center">
          {[
            { color: '#1E3A8A', label: '0-10%' },
            { color: '#2563EB', label: '10-20%' },
            { color: '#38BDF8', label: '20-30%' },
            { color: '#2DD4BF', label: '30-40%' },
            { color: '#86EFAC', label: '40-50%' },
            { color: '#FDE047', label: '50-60%' },
            { color: '#FB923C', label: '60-70%' },
            { color: '#F97316', label: '70-80%' },
            { color: '#EF4444', label: '80-90%' },
            { color: '#991B1B', label: '90-100%' }
          ].map(({ color, label }) => (
            <div key={label} className="flex flex-col items-center" title={label}>
              <div className="w-6 h-3 rounded" style={{ backgroundColor: color }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[9px] text-slate-400 mt-1 px-1">
          <span>Low</span>
          <span>High</span>
        </div>

        {/* Weird Area Legend */}
        {showWeirdArea && (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded border-2 border-black" style={{ backgroundColor: '#000000', opacity: 0.2 }} />
              <span className="text-[10px] text-gray-900 font-medium">Weird Area</span>
            </div>
          </div>
        )}

        {/* KMZ Overlay Legend */}
        {kmzOverlay && kmzOverlay.visible && (
          <div className="mt-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-5 h-2 rounded border border-orange-600" style={{ backgroundColor: '#F97316', opacity: 0.5 }} />
              <span className="text-[10px] text-orange-700 font-medium">KMZ Overlay</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Get color based on percentile
const getPercentileColor = (demand, percentiles) => {
  const { p10, p20, p30, p40, p50, p60, p70, p80, p90 } = percentiles;

  if (demand <= p10) return '#1E3A8A';
  if (demand <= p20) return '#2563EB';
  if (demand <= p30) return '#38BDF8';
  if (demand <= p40) return '#2DD4BF';
  if (demand <= p50) return '#86EFAC';
  if (demand <= p60) return '#FDE047';
  if (demand <= p70) return '#FB923C';
  if (demand <= p80) return '#F97316';
  if (demand <= p90) return '#EF4444';
  return '#991B1B';
};

const FullscreenMap = () => {
  const [showServiceArea, setShowServiceArea] = useState(true);
  const [showWeirdArea, setShowWeirdArea] = useState(false);
  const [hexOpacity, setHexOpacity] = useState(DEFAULT_OPACITY);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [drawnLayers, setDrawnLayers] = useState([]);
  const [weirdAreaCoords, setWeirdAreaCoords] = useState({ type: 'single', coordinates: defaultWeirdAreaCoordinates });
  const [kmzOverlay, setKmzOverlay] = useState(null);
  const featureGroupRef = useRef(null);

  // Parse weird area coords to handle both old and new format
  const parseWeirdAreaCoords = (coords) => {
    if (!coords) {
      return { type: 'single', polygons: [defaultWeirdAreaCoordinates] };
    }
    // New format with type
    if (coords.type === 'multi') {
      return { type: 'multi', polygons: coords.coordinates };
    }
    if (coords.type === 'single') {
      return { type: 'single', polygons: [coords.coordinates] };
    }
    // Old format - plain array
    if (Array.isArray(coords) && coords.length > 0) {
      return { type: 'single', polygons: [coords] };
    }
    return { type: 'single', polygons: [defaultWeirdAreaCoordinates] };
  };

  const weirdAreaData = parseWeirdAreaCoords(weirdAreaCoords);
  // For backwards compatibility
  const weirdAreaCoordsFlat = weirdAreaData.polygons[0];

  // Filter states
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [locationType, setLocationType] = useState('PICKUP');
  const [dateRange, setDateRange] = useState(null);

  // Get data from localStorage
  const mapData = useMemo(() => {
    try {
      const saved = localStorage.getItem('rideflow_map_data');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading map data:', e);
    }
    return null;
  }, []);

  // Handle draw events
  const handleCreated = (e) => {
    const { layer } = e;
    const coords = layer.getLatLngs();

    let flatCoords = coords;
    if (coords[0] && Array.isArray(coords[0]) && coords[0][0] && typeof coords[0][0].lat === 'number') {
      flatCoords = coords[0];
    } else if (coords[0] && typeof coords[0].lat === 'number') {
      flatCoords = coords;
    }

    const newCoords = flatCoords.map(c => [c.lat, c.lng]);
    setDrawnLayers(prev => [...prev, newCoords]);
  };

  const handleDeleted = () => {
    setDrawnLayers([]);
  };

  // Merge polygons using Turf.js union
  const mergePolygonsWithTurf = (original, additions) => {
    // Convert Leaflet coordinates [lat, lng] to GeoJSON [lng, lat]
    const toGeoJSONCoords = (coords) => {
      const geoCoords = coords.map(([lat, lng]) => [lng, lat]);
      // Ensure polygon is closed
      if (geoCoords.length > 0 &&
        (geoCoords[0][0] !== geoCoords[geoCoords.length - 1][0] ||
          geoCoords[0][1] !== geoCoords[geoCoords.length - 1][1])) {
        geoCoords.push([...geoCoords[0]]);
      }
      return geoCoords;
    };

    // Convert GeoJSON coordinates back to Leaflet [lat, lng]
    const toLeafletCoords = (geoCoords) => {
      return geoCoords.map(([lng, lat]) => [lat, lng]);
    };

    // Create Turf polygon from original weird area
    let mergedPolygon = turf.polygon([toGeoJSONCoords(original)]);

    // Union each drawn polygon with the result
    for (const drawnCoords of additions) {
      try {
        const drawnPolygon = turf.polygon([toGeoJSONCoords(drawnCoords)]);
        mergedPolygon = turf.union(turf.featureCollection([mergedPolygon, drawnPolygon]));
      } catch (error) {
        console.error('Error unioning polygon:', error);
        continue;
      }
    }

    // Extract coordinates from merged polygon
    if (mergedPolygon && mergedPolygon.geometry) {
      const coords = mergedPolygon.geometry.coordinates;
      if (mergedPolygon.geometry.type === 'Polygon') {
        return { type: 'single', coordinates: toLeafletCoords(coords[0]) };
      } else if (mergedPolygon.geometry.type === 'MultiPolygon') {
        // For MultiPolygon (non-overlapping shapes), preserve ALL exact shapes
        // Return array of polygons to maintain exact outlines
        const allPolygons = coords.map(poly => toLeafletCoords(poly[0]));
        return { type: 'multi', coordinates: allPolygons };
      }
    }

    return { type: 'single', coordinates: original };
  };

  // Handle save/cancel edit mode
  const handleToggleEditMode = (value) => {
    if (isEditMode && !value) {
      // Save - merge drawn layers using Turf.js union
      if (drawnLayers.length > 0) {
        try {
          const mergedCoords = mergePolygonsWithTurf(weirdAreaCoordsFlat, drawnLayers);
          setWeirdAreaCoords(mergedCoords);
        } catch (error) {
          console.error('Error merging polygons:', error);
          alert('Error merging polygons. Please try drawing again.');
        }
      }
      setDrawnLayers([]);
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
      }
    } else if (value === false) {
      // Cancel
      setDrawnLayers([]);
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
      }
    }
    setIsEditMode(value);
  };

  // Export KMZ - handles multiple polygons
  const handleExportKmz = async () => {
    const placemarks = weirdAreaData.polygons.map((coordinates, idx) => {
      const coordString = coordinates
        .map(([lat, lng]) => `${lng},${lat},0`)
        .join('\n              ');

      return `    <Placemark>
      <name>Reworked Service Area${weirdAreaData.polygons.length > 1 ? ` Part ${idx + 1}` : ''}</name>
      <styleUrl>#serviceAreaStyle</styleUrl>
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>
              ${coordString}
            </coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>`;
    }).join('\n');

    const kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Reworked Service Area</name>
    <Style id="serviceAreaStyle">
      <LineStyle>
        <color>ff000000</color>
        <width>5</width>
      </LineStyle>
      <PolyStyle>
        <color>33000000</color>
      </PolyStyle>
    </Style>
${placemarks}
  </Document>
</kml>`;

    const zip = new JSZip();
    zip.file('doc.kml', kmlContent);

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'reworked_service_area.kmz';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter and process data
  const { clusters, percentiles } = useMemo(() => {
    if (!mapData?.clusters) {
      return { clusters: [], percentiles: {} };
    }

    let filtered = [...mapData.clusters];

    // Apply filters
    if (statusFilter !== 'ALL') {
      // Filter by status if needed
    }

    const demands = filtered.map(c => c.demand).filter(d => d > 0).sort((a, b) => a - b);
    const getPercentile = (arr, p) => arr[Math.floor(arr.length * p / 100)] || 0;

    return {
      clusters: filtered,
      percentiles: {
        p10: getPercentile(demands, 10),
        p20: getPercentile(demands, 20),
        p30: getPercentile(demands, 30),
        p40: getPercentile(demands, 40),
        p50: getPercentile(demands, 50),
        p60: getPercentile(demands, 60),
        p70: getPercentile(demands, 70),
        p80: getPercentile(demands, 80),
        p90: getPercentile(demands, 90)
      }
    };
  }, [mapData, statusFilter]);

  // Calculate sum of demands inside Weird Area (including partially overlapping hexes)
  const weirdAreaDemandSum = useMemo(() => {
    if (!showWeirdArea || !clusters.length || !weirdAreaData.polygons.length) {
      return { total: 0, hexCount: 0 };
    }

    try {
      // Convert all weird area polygons to Turf polygons
      const weirdAreaPolygons = weirdAreaData.polygons.map(coords => {
        const geoCoords = coords.map(([lat, lng]) => [lng, lat]);
        // Ensure polygon is closed
        if (geoCoords.length > 0 &&
          (geoCoords[0][0] !== geoCoords[geoCoords.length - 1][0] ||
            geoCoords[0][1] !== geoCoords[geoCoords.length - 1][1])) {
          geoCoords.push([...geoCoords[0]]);
        }
        return turf.polygon([geoCoords]);
      });

      let totalDemand = 0;
      let hexCount = 0;

      clusters.forEach(cluster => {
        try {
          // Get hex boundary
          const boundary = cellToBoundary(cluster.hexId);
          const hexCoords = boundary.map(([lat, lng]) => [lng, lat]);
          // Close the hex polygon
          hexCoords.push([...hexCoords[0]]);
          const hexPolygon = turf.polygon([hexCoords]);

          // Check if hex intersects with any weird area polygon
          for (const weirdPoly of weirdAreaPolygons) {
            // Use booleanIntersects to check if hex touches or overlaps the weird area
            if (turf.booleanIntersects(hexPolygon, weirdPoly)) {
              totalDemand += cluster.demand;
              hexCount++;
              break; // Only count each hex once
            }
          }
        } catch (e) {
          // Skip invalid hexes
        }
      });

      return { total: totalDemand, hexCount };
    } catch (e) {
      console.error('Error calculating weird area demand:', e);
      return { total: 0, hexCount: 0 };
    }
  }, [showWeirdArea, clusters, weirdAreaData]);

  if (!mapData) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <Globe className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No Map Data Available</h2>
          <p className="text-slate-500 mb-4">Please open the fullscreen map from the Hex Hotspots page</p>
          <a
            href="/dashboard/hex-hotspots"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Hex Hotspots
          </a>
        </div>
      </div>
    );
  }

  const centerLat = mapData.center?.[0] || SERVICE_AREA_CENTER[0];
  const centerLng = mapData.center?.[1] || SERVICE_AREA_CENTER[1];

  return (
    <div className="h-screen w-screen relative">
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={SERVICE_AREA_ZOOM}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
        />

        {/* Map Controls */}
        <MapControls
          showServiceArea={showServiceArea}
          setShowServiceArea={setShowServiceArea}
          showWeirdArea={showWeirdArea}
          setShowWeirdArea={setShowWeirdArea}
          hexOpacity={hexOpacity}
          setHexOpacity={setHexOpacity}
          isEditMode={isEditMode}
          setIsEditMode={handleToggleEditMode}
          onExportKmz={handleExportKmz}
          kmzOverlay={kmzOverlay}
          onKmzUpload={setKmzOverlay}
          onKmzToggle={() => setKmzOverlay(prev => prev ? { ...prev, visible: !prev.visible } : null)}
          onKmzRemove={() => setKmzOverlay(null)}
          onImportWeirdAreaKmz={setWeirdAreaCoords}
          weirdAreaDemandSum={weirdAreaDemandSum}
        />

        {/* Drawing controls - shown when in edit mode */}
        {isEditMode && (
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topleft"
              onCreated={handleCreated}
              onDeleted={handleDeleted}
              draw={{
                rectangle: false,
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
                polygon: {
                  allowIntersection: false,
                  shapeOptions: {
                    color: '#000000',
                    fillColor: '#000000',
                    fillOpacity: 0.2,
                    weight: 3
                  }
                }
              }}
              edit={{
                edit: false,
                remove: true
              }}
            />
          </FeatureGroup>
        )}

        {/* Drawn polygons preview */}
        {drawnLayers.map((coords, idx) => (
          <Polygon
            key={`drawn-${idx}`}
            positions={coords}
            pathOptions={{
              color: '#22C55E',
              fillColor: '#22C55E',
              fillOpacity: 0.3,
              weight: 3,
              dashArray: '5, 5'
            }}
          >
            <Tooltip>
              <div className="text-xs font-semibold text-green-700">
                New Area {idx + 1} (to be merged)
              </div>
            </Tooltip>
          </Polygon>
        ))}

        {/* Hex polygons */}
        {clusters.map((cluster) => {
          const { hexId, demand } = cluster;

          try {
            const boundary = cellToBoundary(hexId);
            const positions = boundary.map(([lat, lng]) => [lat, lng]);
            const color = getPercentileColor(demand, percentiles);

            const centerLat = positions.reduce((sum, p) => sum + p[0], 0) / positions.length;
            const centerLng = positions.reduce((sum, p) => sum + p[1], 0) / positions.length;

            return (
              <React.Fragment key={hexId}>
                <Polygon
                  positions={positions}
                  pathOptions={{
                    color: color,
                    fillColor: color,
                    fillOpacity: hexOpacity,
                    weight: 1,
                    stroke: true,
                    opacity: 0.8
                  }}
                />
                <Marker
                  position={[centerLat, centerLng]}
                  icon={createDemandLabel(demand, color)}
                />
              </React.Fragment>
            );
          } catch (e) {
            return null;
          }
        })}

        {/* Service Area Overlay (Fish Bowl) */}
        {showServiceArea && (
          <Polygon
            positions={serviceAreaCoordinates}
            pathOptions={{
              color: '#6366F1',
              fillColor: '#6366F1',
              fillOpacity: 0.15,
              weight: 3,
              stroke: true,
              opacity: 0.8,
              dashArray: '10, 5'
            }}
          />
        )}

        {/* Weird Area Overlay - BLACK color with thick border */}
        {showWeirdArea && weirdAreaData.polygons.map((polygonCoords, idx) => (
          <Polygon
            key={`weird-area-${idx}`}
            positions={polygonCoords}
            pathOptions={{
              color: '#000000',
              fillColor: '#000000',
              fillOpacity: 0.1,
              weight: 5,
              stroke: true,
              opacity: 1
            }}
          >
            <Tooltip>
              <div className="text-xs font-semibold text-gray-900">
                Weird Area Boundary {weirdAreaData.polygons.length > 1 ? `(Part ${idx + 1})` : ''}
              </div>
            </Tooltip>
          </Polygon>
        ))}

        {/* KMZ Overlay */}
        {kmzOverlay && kmzOverlay.visible && (
          <>
            {kmzOverlay.polygons?.map((poly, idx) => (
              <Polygon
                key={`kmz-poly-${idx}`}
                positions={poly.coordinates}
                pathOptions={{
                  color: '#F97316',
                  fillColor: '#F97316',
                  fillOpacity: 0.25,
                  weight: 3,
                  stroke: true,
                  opacity: 0.9
                }}
              >
                <Tooltip>
                  <div className="text-xs font-semibold text-orange-700">
                    {poly.name || `KMZ Region ${idx + 1}`}
                  </div>
                </Tooltip>
              </Polygon>
            ))}
          </>
        )}
      </MapContainer>

      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="absolute top-4 left-4 z-[1000] bg-white hover:bg-slate-50 border border-slate-200 rounded-lg p-2 shadow-lg transition-all"
        title={isSidebarOpen ? 'Close filters' : 'Open filters'}
      >
        {isSidebarOpen ? <PanelRightClose className="w-5 h-5 text-slate-600" /> : <PanelRightOpen className="w-5 h-5 text-slate-600" />}
      </button>

      {/* Left Sidebar - Filters */}
      <div className={`absolute top-0 left-0 h-full bg-white border-r border-slate-200 shadow-xl z-[999] transition-all duration-300 ${isSidebarOpen ? 'w-72 opacity-100' : 'w-0 opacity-0 overflow-hidden'}`}>
        <div className="p-4 h-full overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Filter className="w-5 h-5 text-indigo-600" />
              Map Filters
            </h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Time of Day Filter */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Time of Day</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'all', label: 'All Day', icon: Globe },
                { value: 'morning', label: 'Morning', icon: Sun },
                { value: 'evening', label: 'Evening', icon: Moon }
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setTimeFilter(value)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1 ${timeFilter === value
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Location Type Filter */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Location Type</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'PICKUP', label: 'Pickup' },
                { value: 'DROP', label: 'Drop' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setLocationType(value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${locationType === value
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                    : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Ride Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="ALL">All Statuses</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="DNF">DNF</option>
            </select>
          </div>

          {/* Stats */}
          <div className="mt-auto pt-4 border-t border-slate-200">
            <div className="text-xs text-slate-500 space-y-1">
              <div>Total Hexes: <span className="font-semibold text-slate-700">{clusters.length}</span></div>
              <div>Total Demand: <span className="font-semibold text-slate-700">{clusters.reduce((sum, c) => sum + c.demand, 0).toLocaleString()}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <a
        href="/dashboard/hex-hotspots"
        className="absolute bottom-6 left-4 z-[1000] bg-white hover:bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-medium text-slate-700 transition-all"
      >
        <X className="w-4 h-4" />
        Exit Fullscreen
      </a>
    </div>
  );
};

export default FullscreenMap;
